import { create } from "@bufbuild/protobuf"
import type { Context } from "../.."
import type { Note } from "../../../../proto/qf/types_pb"
import { NoteSchema } from "../../../../proto/qf/types_pb"
import type { NoteScope } from "./state"

/* NoteTarget identifies which entity a new note is attached to.
   Exactly one of the fields should be set. */
export type NoteTarget = { SubmissionID?: bigint, GroupID?: bigint, EnrollmentID?: bigint }

/* getNotes fetches all notes relevant to the currently selected submission,
   including the associated group and enrollment notes. */
export const getNotes = async ({ state, effects }: Context): Promise<void> => {
    const submission = state.selectedSubmission
    if (!submission || !state.activeCourse) {
        return
    }
    const response = await effects.global.api.client.getNotes({
        courseID: state.activeCourse,
        submissionID: submission.ID,
    })
    if (response.error) {
        return
    }
    const notes = new Map(state.notes.notes)
    notes.set(submission.ID, response.message.notes)
    state.notes.notes = notes
}

/* getCourseNotes fetches every note in the active course, used by staff
   overviews such as the members page to show per-student notes. */
export const getCourseNotes = async ({ state, effects }: Context): Promise<void> => {
    if (!state.activeCourse) {
        return
    }
    const response = await effects.global.api.client.getCourseNotes({ courseID: state.activeCourse })
    if (response.error) {
        return
    }
    state.notes.courseNotes = response.message.notes
}

/* refresh reloads the note list the calling view is showing, so a mutation
   updates that view. The scope is passed in by the view rather than tracked in
   state, so a background load elsewhere cannot redirect the refresh. */
export const refresh = async ({ actions }: Context, scope: NoteScope): Promise<void> => {
    if (scope === "course") {
        await actions.notes.getCourseNotes()
    } else {
        await actions.notes.getNotes()
    }
}

/* createNote creates a note attached to the given target (submission, group, or
   enrollment) and refreshes the notes for the current submission. */
export const createNote = async ({ state, actions, effects }: Context, { target, scope }: { target: NoteTarget, scope: NoteScope }): Promise<void> => {
    const body = state.notes.draft.trim()
    if (!body || !state.activeCourse) {
        return
    }
    const note = create(NoteSchema, { CourseID: state.activeCourse, body, ...target })
    const response = await effects.global.api.client.createNote(note)
    if (response.error) {
        return
    }
    state.notes.draft = ""
    await actions.notes.refresh(scope)
}

/* updateNote saves an edited note body. Only the note's author may succeed. */
export const updateNote = async ({ state, actions, effects }: Context, { note, scope }: { note: Note, scope: NoteScope }): Promise<void> => {
    const body = state.notes.editDraft.trim()
    if (!body || !state.activeCourse) {
        return
    }
    const response = await effects.global.api.client.updateNote({ ...note, body })
    if (response.error) {
        return
    }
    state.notes.editing = 0n
    state.notes.editDraft = ""
    await actions.notes.refresh(scope)
}

/* deleteNote removes a note. Only the note's author may succeed. */
export const deleteNote = async ({ state, actions, effects }: Context, { note, scope }: { note: Note, scope: NoteScope }): Promise<void> => {
    if (!state.activeCourse || !confirm("Are you sure you want to delete this note?")) {
        return
    }
    const response = await effects.global.api.client.deleteNote(note)
    if (response.error) {
        return
    }
    await actions.notes.refresh(scope)
}

/* startEditing prepares the form to edit an existing note. The new-note draft is
   left alone so that an unsent note is not lost by starting an edit. */
export const startEditing = ({ state }: Context, note: Note): void => {
    state.notes.editing = note.ID
    state.notes.editDraft = note.body
}

/* cancelEditing discards the in-progress edit, keeping the new-note draft. */
export const cancelEditing = ({ state }: Context): void => {
    state.notes.editing = 0n
    state.notes.editDraft = ""
}

/* setDraft updates the body of the new note being drafted. */
export const setDraft = ({ state }: Context, body: string): void => {
    state.notes.draft = body
}

/* setEditDraft updates the body of the note being edited. */
export const setEditDraft = ({ state }: Context, body: string): void => {
    state.notes.editDraft = body
}
